import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowClockwise, Users } from "@phosphor-icons/react";

/**
 * "On Now" — who is currently on duty.
 *
 * Renders every tour whose window covers the current server time and that has
 * an assigned member. Grouped by position (TOUR 1 / TOUR 2 / …) so dispatch
 * can see rig staffing at a glance. Auto-refreshes every 60 seconds.
 */
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Map the raw assignment (ambulance_role / position) to the plain-text
 *  label BSVAC wants on the On-Duty screen. */
function displayRole(t) {
  const raw = `${t.ambulance_role || ""} ${t.position || ""} ${t.title || ""}`.toLowerCase();
  if (raw.includes("communications") || raw.includes("dispatch")) return "Dispatcher";
  if (raw.includes("tour commander") || raw.includes("commander")) return "TOUR COMMANDER";
  if (raw.includes("supervisor")) return "TOUR SUPERVISOR";
  return t.ambulance_role || "—";
}

function fmtDay(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function elapsedLabel(startIso) {
  const start = new Date(startIso).getTime();
  const mins = Math.max(0, Math.floor((Date.now() - start) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m in`;
}

function remainingLabel(endIso) {
  const end = new Date(endIso).getTime();
  const mins = Math.max(0, Math.floor((end - Date.now()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m left`;
}

export default function OnNow() {
  const [data, setData] = useState({ count: 0, tours: [], as_of: null });
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const res = await api.get("/tours/on-now");
      setData(res.data || { count: 0, tours: [] });
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Group by position
  const byPosition = data.tours.reduce((acc, t) => {
    const key = t.position || t.title || "OTHER";
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});
  const positions = Object.keys(byPosition).sort();

  return (
    <AppLayout
      title="On Now"
      subtitle={data.as_of ? `Live roster · updated ${fmtTime(data.as_of)}` : "Live roster"}
      actions={
        <Button variant="outline" onClick={load} disabled={loading} className="rounded-sm" data-testid="on-now-refresh">
          <ArrowClockwise size={14} weight="bold" className="mr-1" /> {loading ? "…" : "Refresh"}
        </Button>
      }
    >
      <div className="mb-4 flex items-center gap-3 text-sm text-slate-600" data-testid="on-now-summary">
        <Users size={18} weight="bold" className="text-emerald-700" />
        <span>
          <strong className="font-display text-lg text-slate-900">{data.count}</strong> member{data.count === 1 ? "" : "s"} currently on duty
        </span>
      </div>

      {positions.length === 0 ? (
        <div className="surface-grid p-8 text-center text-slate-500" data-testid="on-now-empty">
          No one is on duty right now.
        </div>
      ) : (
        <div className="space-y-4">
          {positions.map((pos) => (
            <div key={pos} className="surface-grid" data-testid={`on-now-position-${pos.replace(/\s+/g, "-")}`}>
              <div className="px-4 py-2 bg-slate-900 text-white font-display font-bold tracking-tight uppercase text-sm">
                {pos}
                <span className="ml-2 text-[10px] mono opacity-70">{byPosition[pos].length} on duty</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left tab-tag text-slate-500">
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Cert</th>
                    <th className="px-4 py-2">Shift</th>
                    <th className="px-4 py-2 text-right">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {byPosition[pos].map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`on-now-row-${t.id}`}>
                      <td className="px-4 py-2 font-medium text-slate-900">{t.assigned_user_name || "—"}</td>
                      <td className="px-4 py-2 font-medium text-slate-900" data-testid={`on-now-role-${t.id}`}>{displayRole(t)}</td>
                      <td className="px-4 py-2 mono text-xs uppercase">{t.assigned_user_cert || t.required_certification || "—"}</td>
                      <td className="px-4 py-2 mono text-xs">
                        <div>{fmtTime(t.start_time)} – {fmtTime(t.end_time)}</div>
                        <div className="text-[10px] text-slate-500">{fmtDay(t.start_time)}</div>
                      </td>
                      <td className="px-4 py-2 text-right mono text-xs">
                        <div className="text-emerald-700 font-semibold">{elapsedLabel(t.start_time)}</div>
                        <div className="text-[10px] text-slate-500">{remainingLabel(t.end_time)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
